// Centralised locale + timezone for the app.
// The business is in Melbourne: every date shows as DD-MMM-YYYY (e.g. 28-Oct-2026)
// in Melbourne time, regardless of the user's browser locale.
export const LOCALE = "en-AU";
export const TZ = "Australia/Melbourne";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;
const PARTS = new Intl.DateTimeFormat("en-AU", {
  timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
});
const pad = (n) => String(n).padStart(2, "0");
const dayStr = (y, m, d) => `${pad(d)}-${MON[m - 1]}-${y}`;
const timeStr = (h, min) => `${h % 12 || 12}:${pad(min)} ${h < 12 ? "am" : "pm"}`;

/** Melbourne calendar parts of a Date. */
function melParts(d) {
  const p = Object.fromEntries(PARTS.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, min: +p.minute };
}

const toDate = (v) => (v instanceof Date ? v : new Date(v));

/** DD-MMM-YYYY. Date-only strings (YYYY-MM-DD) are shown as-is, never shifted by timezone. */
export function fmtDate(v) {
  if (v === null || v === undefined || v === "") return "—";
  const m = typeof v === "string" && ISO_DAY.exec(v);
  if (m) return dayStr(+m[1], +m[2], +m[3]);
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = melParts(d);
  return dayStr(p.y, p.m, p.d);
}

/** DD-MMM-YYYY h:mm am/pm in Melbourne time. */
export function fmtDateTime(v) {
  if (v === null || v === undefined || v === "") return "—";
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = melParts(d);
  return `${dayStr(p.y, p.m, p.d)} ${timeStr(p.h, p.min)}`;
}

/** YYYY-MM-DD of a moment in Melbourne (use instead of toISOString().slice(0, 10), which is UTC). */
export function isoDate(d = new Date()) {
  const p = melParts(toDate(d));
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

export const todayISO = () => isoDate(new Date());

/** "YYYY-MM-DDTHH:mm" wall-clock value (datetime-local) → "DD-MMM-YYYY h:mm am". */
export function fmtLocalValue(v) {
  const m = ISO_LOCAL.exec(v || "");
  return m ? `${dayStr(+m[1], +m[2], +m[3])} ${timeStr(+m[4], +m[5])}` : "";
}

/** "YYYY-MM-DDTHH:mm" read as Melbourne wall-clock time → UTC ISO string ("" when blank/invalid). */
export function melLocalToISO(v) {
  const m = ISO_LOCAL.exec(v || "");
  if (!m) return "";
  const want = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let t = want;
  for (let i = 0; i < 2; i += 1) {
    const p = melParts(new Date(t));
    t += want - Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
  }
  return new Date(t).toISOString();
}

/** Current Melbourne time as HH:mm. */
export function melNowHHMM() {
  const p = melParts(new Date());
  return `${pad(p.h)}:${pad(p.min)}`;
}

/** Date → "YYYY-MM-DDTHH:mm" in Melbourne, for datetime-local values. */
export function toLocalValue(v) {
  if (!v) return "";
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return "";
  const p = melParts(d);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}T${pad(p.h)}:${pad(p.min)}`;
}

const daysIn = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Typed day (28-Oct-2026, 28/10/2026, 28 October 26, 2026-10-28) → "YYYY-MM-DD"; "" when blank, null when invalid. */
export function parseDay(text) {
  const s = (text || "").trim();
  if (!s) return "";
  let y, m, d, r;
  if ((r = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s))) [y, m, d] = [+r[1], +r[2], +r[3]];
  else if ((r = /^(\d{1,2})[\s\-/.]+([A-Za-z]{3,9}|\d{1,2})[\s\-/.,]+(\d{4}|\d{2})$/.exec(s))) {
    d = +r[1];
    m = /\d/.test(r[2]) ? +r[2] : MON.findIndex((x) => x.toLowerCase() === r[2].slice(0, 3).toLowerCase()) + 1;
    y = +r[3];
    if (r[3].length === 2) y += y <= (new Date().getFullYear() % 100) + 10 ? 2000 : 1900;
  } else return null;
  if (!m || m > 12 || !d || d > daysIn(y, m) || y < 1900) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** Typed time (9, 9am, 9:30 pm, 21:30, 0930) → "HH:mm" or null. */
export function parseTime(text) {
  const r = /^(\d{1,2})(?::?(\d{2}))?\s*(am|pm|a|p)?$/i.exec((text || "").trim());
  if (!r) return null;
  let h = +r[1];
  const min = +(r[2] || 0);
  const ap = (r[3] || "").toLowerCase();
  if (ap && (h < 1 || h > 12)) return null;
  if (ap.startsWith("p") && h < 12) h += 12;
  if (ap.startsWith("a") && h === 12) h = 0;
  return h > 23 || min > 59 ? null : `${pad(h)}:${pad(min)}`;
}

/** Typed date + optional time → "YYYY-MM-DDTHH:mm" (time defaults to the previous value's or 9:00 am). */
export function parseDateTime(text, prev = "") {
  const s = (text || "").trim();
  if (!s) return "";
  const iso = ISO_LOCAL.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}T${iso[4]}:${iso[5]}`;
  const r = /^(.*?\d)(?:[\s,]+|T)(\d{1,2}(?::?\d{2})?\s*(?:am|pm|a|p)?)$/i.exec(s);
  const day = parseDay(r ? r[1] : s);
  if (!day) {
    const whole = parseDay(s);
    return whole ? `${whole}T${(ISO_LOCAL.exec(prev) || [])[4] ? prev.slice(11, 16) : "09:00"}` : null;
  }
  const time = r ? parseTime(r[2]) : (ISO_LOCAL.exec(prev) ? prev.slice(11, 16) : "09:00");
  return time ? `${day}T${time}` : null;
}

/** ISO date / datetime strings → DD-MMM-YYYY (time kept for datetimes); anything else unchanged. */
export function fmtIfDate(v) {
  if (typeof v !== "string") return v;
  if (ISO_DAY.test(v)) return fmtDate(v);
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) && !Number.isNaN(new Date(v).getTime()) ? fmtDateTime(v) : v;
}

/** AUD money with AU locale. */
export function fmtMoney(n) {
  return Number(n || 0).toLocaleString(LOCALE, {
    style: "currency",
    currency: "AUD",
  });
}
